/**
 * Manual pricebook attribution reached from an agent's pricebook proposal
 * rather than from the document's line list — for the review queue, whose
 * rows carry the proposal but not the document's lines. The server names the
 * lines the proposal is about (`line_item_ids`); see `pricebook-match-form.tsx`
 * for the pick itself.
 */
import { useState } from "react";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import { Button } from "@/core/components/ui/button";
import { DialogFooter } from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import {
  useGetPurchaseDocument,
  useMatchLinePricebookItem,
} from "@/accounts-payable/query";
import type { ApAgentAction, ApDocumentType } from "@/accounts-payable/types";
import {
  documentLineItems,
  documentTypeLabel,
  pricebookProposalLinkLabel,
  pricebookProposalSku,
  proposalLinkAriaLabel,
} from "@/accounts-payable/utils";
import { PricebookMatchDialog } from "./pricebook-match-dialog";
import { PricebookMatchForm } from "./pricebook-match-form";
import { ProposalLineChooser } from "./proposal-line-chooser";
import { TextButton } from "./shared";

/**
 * Picking the line's item rejects the proposal and has the agent re-check,
 * so this is the way out when the proposal is aimed at the wrong item.
 * Renders nothing for a proposal that names no line on the document.
 */
export const ProposalPricebookMatchButton = ({
  businessId,
  documentId,
  documentType,
  documentNumber,
  agentRunId,
  action,
  appearance = "button",
  className,
}: {
  businessId: string | null;
  documentId: string;
  documentType: ApDocumentType;
  /** Names the document in the accessible name, where a list repeats the link. */
  documentNumber?: string | null;
  /** The document's newest run id, to tell whether the pick queued a new one. */
  agentRunId: string | null;
  action: ApAgentAction;
  /** A grey pill among a card's buttons, or a link beside a row's details. */
  appearance?: "button" | "link";
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  const matchLine = useMatchLinePricebookItem(businessId, documentType);
  const label = pricebookProposalLinkLabel(action);
  if (!label) return null;

  const kind = documentTypeLabel(documentType).lower;
  const ariaLabel = proposalLinkAriaLabel(
    label,
    `choose the pricebook item for ${pricebookProposalSku(action)}${documentNumber ? ` on ${kind} ${documentNumber}` : ""}`,
  );
  const trigger =
    appearance === "link" ? (
      <TextButton
        onClick={() => setOpen(true)}
        aria-label={ariaLabel}
        className={className}
      >
        {label}
      </TextButton>
    ) : (
      <Button
        size="xsPill"
        variant="tertiary"
        onClick={() => setOpen(true)}
        aria-label={ariaLabel}
        className={className}
      >
        {label}
      </Button>
    );

  return (
    <>
      {trigger}
      <PricebookMatchDialog
        open={open}
        onOpenChange={setOpen}
        saving={matchLine.isPending}
      >
        <ProposalLineStep
          businessId={businessId}
          documentId={documentId}
          documentType={documentType}
          agentRunId={agentRunId}
          lineItemIds={action.line_item_ids ?? []}
          matchLine={matchLine}
          onClose={() => setOpen(false)}
        />
      </PricebookMatchDialog>
    </>
  );
};

/**
 * Loads the document, since a review row only carries its header, then picks
 * for the proposal's line. A proposal about several lines (one SKU) first asks
 * which: they are usually the same part, but not always, and the pick form
 * offers to apply it to the others.
 */
const ProposalLineStep = ({
  businessId,
  documentId,
  documentType,
  agentRunId,
  lineItemIds,
  matchLine,
  onClose,
}: {
  businessId: string | null;
  documentId: string;
  documentType: ApDocumentType;
  agentRunId: string | null;
  lineItemIds: string[];
  matchLine: ReturnType<typeof useMatchLinePricebookItem>;
  onClose: () => void;
}) => {
  const {
    data: document,
    isError,
    isFetching,
    refetch,
  } = useGetPurchaseDocument(businessId, documentType, documentId);
  const [chosenId, setChosenId] = useState<string | null>(null);
  const kind = documentTypeLabel(documentType).lower;
  const lines = document
    ? documentLineItems(document).filter((item) =>
        lineItemIds.includes(item.id),
      )
    : [];
  const line =
    lines.length === 1 ? lines[0] : lines.find((item) => item.id === chosenId);

  if (lines.length > 1 && !line)
    return (
      <ProposalLineChooser
        lines={lines}
        onChoose={(chosen) => setChosenId(chosen.id)}
        onClose={onClose}
      />
    );

  // Keyed by line: if a refetch moves the pick to another line, its form
  // starts over rather than carrying this one's choices.
  if (line)
    return (
      <PricebookMatchForm
        key={line.id}
        businessId={businessId}
        documentId={documentId}
        documentType={documentType}
        agentRunId={agentRunId}
        item={line}
        matchLine={matchLine}
        onClose={onClose}
      />
    );

  // The document loaded but the line is gone (re-parsed since the run), or
  // the load failed: say which, and let a failure be retried in place.
  const failed = isError || (document && !isFetching);
  return (
    <>
      <Stack gap="md" fullWidth className="px-6">
        {failed ? (
          <Stack
            gap="sm"
            align="center"
            fullWidth
            role="status"
            className="px-4 py-6 text-center text-md text-tertiary-text"
          >
            {isError
              ? `Couldn’t load the ${kind}’s lines.`
              : `This line is no longer on the ${kind}. Open the ${kind} to change its lines’ items.`}
            {isError && (
              <Button
                size="xsPill"
                variant="tertiary"
                loading={isFetching}
                onClick={() => void refetch()}
              >
                Try again
              </Button>
            )}
          </Stack>
        ) : (
          <LoadingSpinner
            containerClassName="py-6"
            aria-label={`Loading the ${kind}’s lines`}
          />
        )}
      </Stack>
      <DialogFooter className="flex-row justify-end sm:space-x-0">
        <Button variant="secondary" size="sm" onClick={onClose}>
          Cancel
        </Button>
      </DialogFooter>
    </>
  );
};
